import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import { SuccessDescription, SuccessTitle } from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ButtonInline } from 'remote-com-shared/components/Button';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import Modal from 'remote-com-shared/components/ModalForm';
import {
  getCurrentURL,
  registerLead,
  createPartnerStackSignup,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  ModalTitle,
  FormError,
  DynamicFormWrapper,
  SubmitButtonWrapper,
  SubsribeSuccessContainer,
} from 'remote-com-shared/components/ModalForm/styles';
import { BookitScript } from 'remote-com-shared/domains/bookit/Bookit';
import {
  composeBookitPayload,
  formFieldsContainBookit,
} from 'remote-com-shared/domains/bookit/helpers';
import { useBookit } from 'remote-com-shared/domains/bookit/hooks';
import { getFields } from 'remote-com-shared/domains/hubspot/fields';
import { valuesHaveField } from 'remote-com-shared/domains/hubspot/helpers';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

export const FormLabel = styled.span`
  ${({ theme }) => theme.typography.bodyXS}
  line-height: 20px;
  color: var(--colors-bayoux);

  a {
    color: var(--colors-irisBlue);
  }
`;

const FormLabelWrapper = styled.div`
  margin-top: -120px;
  margin-bottom: 70px;

  ${FormLabel} {
    font-size: 13px;
  }
`;

const PrivacyPolicyText = ({ legalConsentOptions }) => {
  const privacyPolicytext = JSON.parse(legalConsentOptions.value).privacyPolicyText;

  return (
    <FormLabelWrapper>
      <FormLabel dangerouslySetInnerHTML={{ __html: privacyPolicytext }} />
    </FormLabelWrapper>
  );
};

export function GlobalPayrollForm({ hubspotFormDefinition }) {
  // TODO: use form hook
  const {
    submitText,
    inlineMessage: successMessage = '',
    formFieldGroups,
    metaData,
  } = hubspotFormDefinition;
  const legalConsentOptions = metaData.find(({ name }) => name === 'legalConsentOptions');

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  const reCaptchaRef = useRef(null);
  const impactFormValues = useImpactFormValues();
  const fields = getFields(formFieldGroups, metaData);

  const shouldLoadBookit = formFieldsContainBookit(fields);
  const {
    submit: submitBookit,
    redirect: redirectBookit,
    ready: bookitIsReady,
    onLoad: initializeBookit,
    scriptConfig,
  } = useBookit();

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      const includePartnerstack =
        Object.prototype.hasOwnProperty.call(values, 'pspartnerkey') &&
        Object.prototype.hasOwnProperty.call(values, 'pspartner_group');
      const includeImpactValues = hasImpactFields(values);

      try {
        if (includePartnerstack) {
          try {
            const publicSlug = uuid4();
            await createPartnerStackSignup({
              name: values.name,
              email: publicSlug,
            });
          } catch {
            // Ignore as failing should not break hubspot
          }
        }

        let payload = {
          ...values,
          ...(Object.prototype.hasOwnProperty.call(values, 'utm')
            ? {
                utm: getCurrentURL(),
              }
            : {}),
          ...(includePartnerstack ? getPartnerStackInfo() : {}),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        let canSubmitBookIt = true;
        if (valuesHaveField(values, 'employees_to_onboard')) {
          canSubmitBookIt = parseInt(values.employees_to_onboard, 10) < 10;
        }

        let bookitResponse;
        if (bookitIsReady && canSubmitBookIt) {
          bookitResponse = await submitBookit(payload);
          payload = composeBookitPayload(payload, bookitResponse);
        }

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotFormDefinition.portalId}/${hubspotFormDefinition.guid}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
        });

        setShowLoader(false);
        setShowSuccessState(true);

        if (bookitIsReady && bookitResponse) {
          redirectBookit(bookitResponse);
        }
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <>
      {shouldLoadBookit && <BookitScript onLoad={initializeBookit} scriptConfig={scriptConfig} />}

      <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
        <GlobalStyles />
        <FormContainer>
          {showSuccessState ? (
            <SubsribeSuccessContainer>
              <SuccessMark />
              <SuccessTitle>Thank you!</SuccessTitle>
              <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
            </SubsribeSuccessContainer>
          ) : (
            <DynamicFormWrapper>
              <ModalTitle>Find out more about global payroll with Remote</ModalTitle>
              {showFormError && (
                <FormError>
                  Something went wrong. Please try again, or contact{' '}
                  <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                  error persists. Thank you.
                </FormError>
              )}

              <DynamicForm
                formName="submit-global-payroll"
                fields={fields}
                fieldValues={formValues}
                formError={formState.errorMessage}
                formTheme="dark"
                formSize="small"
                handleOnSubmit={handleSubmit}
                isLoading={formState.isLoading}
                enableReinitialize
              />

              {legalConsentOptions && (
                <PrivacyPolicyText legalConsentOptions={legalConsentOptions} />
              )}

              <CaptchaWrapper>
                <LocaleContextConsumer>
                  {(locale) => (
                    <ReCAPTCHA
                      hl={locale}
                      ref={reCaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                      onChange={handleCaptchaChange}
                    />
                  )}
                </LocaleContextConsumer>
              </CaptchaWrapper>

              <SubmitButtonWrapper>
                <SubmitButton
                  isLoading={showLoader}
                  disabled={!isCaptchaDone}
                  form="submit-global-payroll"
                >
                  {submitText || 'Submit'}
                </SubmitButton>
              </SubmitButtonWrapper>
            </DynamicFormWrapper>
          )}
        </FormContainer>
      </ThemeProvider>
    </>
  );
}

export const GlobalPayrollFormModal = ({ visible, onCancel, ...props }) => (
  <Modal visible={visible} width="660px" footer={null} onCancel={onCancel}>
    <GlobalPayrollForm {...props} />
  </Modal>
);
